import {forwardRef, useState} from "react";
import {Box, IconButton, Typography} from "@mui/material";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import {AddressBadge} from "./AddressBadge";
import {SelectAddressDialog} from "../../utils/uiUtils";

/**
 * AddressPicker — wraps AddressBadge with an optional existing-address picker dialog.
 *
 * Props: same as AddressBadge, plus:
 *   existingAddresses — [{ address_line_1, address_line_2, city, state, country, postal_code, _label? }]
 *                       Parent assembles from already-loaded data (customer list, shipments, etc.)
 *
 * Behaviour:
 * - Renders AddressBadge (starts expanded when value is empty)
 * - If existingAddresses provided + active: injects a "Pick existing" bookmark button into
 *   AddressBadge's label row via the actions prop
 * - Clicking the button opens SelectAddressDialog
 * - Selecting an address fires onChange and collapses the badge
 */
const deduplicateAddresses = (addresses) => {
    if (!addresses?.length) return [];
    const map = {};
    addresses.forEach(addr => {
        const key = addr.uuid || addr.address_uuid || JSON.stringify(addr);
        map[key] = addr;
    });
    return Object.values(map);
};

export const AddressPicker = forwardRef(({value, onChange, onSave, onDelete, label, fields, canToggle = true, active = true, existingAddresses, requiredFields}, ref) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const dedupedAddresses = deduplicateAddresses(existingAddresses);
    const hasExisting = active && dedupedAddresses.length > 0;

    const handleSelect = (addr) => {
        if (onChange) onChange(addr);
        setDialogOpen(false);
    };

    const bookmarkButton = hasExisting ? (
        <IconButton
            size="small"
            onClick={() => setDialogOpen(true)}
            sx={{p: 0.25, color: '#FFC540', '&:hover': {opacity: 0.8}, pointerEvents: 'auto'}}
        >
            <BookmarkBorderOutlinedIcon sx={{fontSize: 14}}/>
            <Typography sx={{fontSize: 11, fontFamily: 'Poppins', ml: 0.25, color: 'inherit'}}>
                Pick existing
            </Typography>
        </IconButton>
    ) : null;

    return (
        <Box>
            <AddressBadge
                ref={ref}
                value={value}
                onChange={onChange}
                onSave={onSave}
                onDelete={onDelete}
                label={label}
                fields={fields}
                canToggle={canToggle}
                active={active}
                actions={bookmarkButton}
                requiredFields={requiredFields}
            />
            {hasExisting && (
                <SelectAddressDialog
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    addresses={dedupedAddresses}
                    onConfirm={handleSelect}
                />
            )}
        </Box>
    );
});
